import Model from '@/store/plugins/orm/Model';
import TaxRate from '@/models/TaxRate';

export class TaxItem extends Model {
    static get entity () {
        return 'tax_items'
    }

    static fields () {
        return {
            id: this.attr(null),
            label: this.attr(null),
            rate: this.attr(null),
            type: this.attr(null),
            name: this.attr(null),
            num_of_items: this.attr(null),
            price_before_tax: this.attr(null),
            value: this.attr(null),
            additional_discount_amount: this.attr(null),
            additional_count_amount: this.attr(null),
            tax_group_revision: this.attr(null),
        }
    }

    /**
     * Make a duplicate of the current tax item.
     */
    $duplicate() {
        return new TaxItem(this.$toJson());
    }

    getNumOfItems() {
        return parseFloat(this.num_of_items);
    }

    getPriceBeforeTax() {
        return Money.parse(this.price_before_tax);
    }

    getValue() {
        return Money.parse(this.value);
    }

    getAdditionalDiscountAmount() {
        return Money.parse(this.additional_discount_amount);
    }

    getAdditionalCountAmount() {
        return Money.parse(this.additional_count_amount);
    }

    sameAs(taxItem) {
        return (!!this.id && this.id === taxItem.id) || this.label === taxItem.label;
    }

    static createForOrderItem(order, orderItem) {
        const taxRate = TaxRate.all().find(taxRate => taxRate.id === orderItem.tax_rate_id);

        if(!taxRate) return null;

        const priceBeforeTax = !order.isCreditNote()
            ? orderItem.getPriceBeforeTax()
            : orderItem.getAdditionalPriceBeforeTax();
        const tax = !order.isCreditNote()
            ? orderItem.getTax()
            : orderItem.getAdditionalTax();
        const additionalDiscountAmount = orderItem.getAdditionalDiscountPercent() != 0
            ? orderItem.getAdditionalPriceBeforeTax()
            : Money.parse(0);
        const additionalCountAmount = orderItem.getAdditionalCount() != 0
            ? orderItem.getAdditionalPriceBeforeTax()
            : Money.parse(0);

        return new TaxItem({
            id: locale === 'sr' || !orderItem.exempt_from_tax ? taxRate.id : null,
            label: locale === 'sr' || !orderItem.exempt_from_tax ? taxRate.label : orderItem.exempt_from_tax,
            rate: locale === 'sr' || !orderItem.exempt_from_tax ? taxRate.rate : '0.00',
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: 1,
            price_before_tax: priceBeforeTax.toFormat('0.0000'),
            value: tax.toFormat('0.0000'),
            additional_discount_amount: additionalDiscountAmount.toFormat('0.0000'),
            additional_count_amount: additionalCountAmount.toFormat('0.0000'),
            tax_group_revision: 1
        });
    }

    static createForTaxRateOnTotal(order, taxRate) {
        const value = order.getToPay().percentage(taxRate.getRate()).toFormat('0.0000')

        return new TaxItem({
            id: taxRate.id,
            label: taxRate.label,
            rate: taxRate.rate,
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: null,
            price_before_tax: null,
            value: Money.parse(value).toFormat('0.0000'),
            additional_discount_amount: null,
            additional_count_amount: null,
            tax_group_revision: 1
        })
    }

    static createForSrFiscalTaxItem(order, taxItem, invoice) {
        const isRefund = invoice.transactionType === 'Refund';
        const taxRate = TaxRate.all().find(t => t.label === taxItem.label);

        const priceBeforeTax = order.items
            .filter(item => item.tax_rate_id == taxRate.id)
            .reduce((acc, cur) => acc.add(cur.getPriceBeforeTax()), Money.parse(0));

        return new TaxItem({
            id: taxRate.id,
            label: taxItem.label,
            rate: Money.parse(taxItem.rate).toFormat('0.00'),
            type: taxRate.type,
            name: taxRate.name,
            num_of_items: invoice.items.filter(item => item.labels.includes(taxItem.label)).length,
            price_before_tax: priceBeforeTax.toFormat('0.0000'),
            value: Money.parse(isRefund ? -1 * taxItem.amount : taxItem.amount).toFormat('0.0000'),
            additional_discount_amount: '0.0000',
            additional_count_amount: '0.0000',
            tax_group_revision: taxItem.taxGroupRevision
        });
    }

    mergeWith(taxItem) {
        this.num_of_items += taxItem.num_of_items;

        if (taxItem.price_before_tax) {
            this.price_before_tax = this.getPriceBeforeTax()
                .add(taxItem.getPriceBeforeTax())
                .toFormat('0.0000');
        }

        if (taxItem.additional_discount_amount) {
            this.additional_discount_amount = this.getAdditionalDiscountAmount()
                .add(taxItem.getAdditionalDiscountAmount())
                .toFormat('0.0000');
        }

        if (taxItem.additional_count_amount) {
            this.additional_count_amount = this.getAdditionalCountAmount()
                .add(taxItem.getAdditionalCountAmount())
                .toFormat('0.0000');
        }

        this.value = this.getValue()
            .add(taxItem.getValue())
            .toFormat('0.0000');;
    }

    static merge(taxItems) {
        const taxes = [];

        taxItems.forEach(t => {
            const taxItem = t.$duplicate();
            const existingTaxItem = taxes.find(item => item.sameAs(taxItem));

            if (!existingTaxItem) {
                taxes.push(taxItem);
            } else {
                existingTaxItem.mergeWith(taxItem)
            }
        })

        return taxes;
    }
}

export default TaxItem
